import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSpRosteringSetting } from "store/selector/sp.selector";
import SessionManager from "Utils/SessionManager";
import JourneyEventBuffer from "Utils/JourneyEventBuffer";
import { parseBooleanVal } from "Utils/utils";
import { getRouteMetadata } from "config/routeMetadata";

const DEBOUNCE_MS = 500;

const JourneyTracker = ({ children }) => {
  const location = useLocation();
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const prevPathRef = useRef(null);
  const prevTimeRef = useRef(null);
  const debounceRef = useRef(null);
  const initializedRef = useRef(false);

  const isEnabled = parseBooleanVal(
    spRosteringSetting?.user_journey_tracking
  );

  useEffect(() => {
    if (!isEnabled) {
      if (initializedRef.current) {
        JourneyEventBuffer.stop();
        initializedRef.current = false;
      }
      return;
    }

    const sessionId = SessionManager.ensureSession();
    if (!sessionId) return;

    if (!initializedRef.current) {
      JourneyEventBuffer.start();
      JourneyEventBuffer.track("session_start", {
        event_data: {
          device: /Mobi|Android/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        },
      });
      initializedRef.current = true;
    }

    return () => {
      // Cleanup on unmount (app teardown)
    };
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled || !initializedRef.current) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const currentPath = location.pathname;

      if (currentPath === prevPathRef.current) return;

      const now = Date.now();
      let durationOnPrevPage = null;
      if (prevTimeRef.current && prevPathRef.current) {
        durationOnPrevPage = Math.round((now - prevTimeRef.current) / 1000);
      }

      const routeMeta = getRouteMetadata(currentPath);
      JourneyEventBuffer.track("page_view", {
        route: currentPath,
        event_data: {
          from_route: prevPathRef.current,
          duration_on_previous: durationOnPrevPage,
          feature_area: routeMeta.featureArea,
          entity_type: routeMeta.entityType,
        },
      });

      prevPathRef.current = currentPath;
      prevTimeRef.current = now;
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [location.pathname, isEnabled]);

  return children || null;
};

export default JourneyTracker;
